import { useCallback, useEffect, useState } from "react";
import { BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Stars } from "./Stars";
import ReviewForm from "./ReviewForm";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });

export default function Reviews() {
  const [data, setData] = useState({ reviews: [], summary: { count: 0, average: 0 } });

  const load = useCallback(() => {
    api.get("/reviews").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { reviews, summary } = data;

  return (
    <section id="ulasan" data-testid="reviews-section" className="py-20 bg-[#F4EFE6] border-b border-[#E8E2D5]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <span className="text-xs uppercase tracking-widest text-[#5F755E] font-semibold">Kepercayaan Pelanggan</span>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-normal text-[#1E2B1D] mt-1">Ulasan Pelanggan</h2>
            <p className="text-xs text-[#6B7C66] mt-2 max-w-md">
              Ulasan asli dari pembeli yang pesanannya sudah selesai — ditandai sebagai pembeli terverifikasi.
            </p>
          </div>
          <div data-testid="reviews-summary" className="flex items-center gap-4 bg-[#FBF9F5] border border-[#E8E2D5] rounded-2xl px-5 py-4">
            <span data-testid="reviews-average" className="font-serif-display text-4xl text-[#1E2B1D] leading-none">
              {summary.count ? summary.average.toFixed(1) : "–"}
            </span>
            <div>
              <Stars value={Math.round(summary.average)} />
              <span data-testid="reviews-count" className="text-[11px] text-[#7E8F7A] block mt-1">
                {summary.count ? `${summary.count} ulasan terverifikasi` : "Belum ada ulasan"}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-start">
          {reviews.length === 0 ? (
            <div data-testid="reviews-empty" className="bg-[#FBF9F5] border border-dashed border-[#C5BCAB] rounded-2xl p-10 text-center text-sm text-[#6B7C66]">
              Jadilah yang pertama memberi ulasan untuk Puspita Florist.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {reviews.map((r) => (
                <article key={r.id} data-testid={`review-card-${r.id}`} className="bg-[#FBF9F5] border border-[#E8E2D5] rounded-2xl p-6 flex flex-col justify-between">
                  <div>
                    <Stars value={r.rating} testId={`review-stars-${r.id}`} />
                    <p className="text-sm italic text-[#354634] leading-relaxed mt-3">“{r.comment}”</p>
                    {r.product_names?.length > 0 && (
                      <p className="text-[11px] text-[#7E8F7A] mt-3">Membeli: {r.product_names.join(", ")}</p>
                    )}
                  </div>
                  <div className="mt-5 pt-4 border-t border-[#E8E2D5] flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-[#E5DEC9] text-xs font-bold text-[#3D5A40] flex items-center justify-center">
                        {r.buyer_name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <span className="text-xs font-bold text-[#1E2B1D] block">{r.buyer_name}</span>
                        <span className="text-[10px] text-[#7E8F7A]">{fmtDate(r.created_at)}</span>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#3D5A40] bg-[#EAF2E9] px-2 py-1 rounded-full">
                      <BadgeCheck className="w-3 h-3" /> Terverifikasi
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
          <ReviewForm onSubmitted={load} />
        </div>
      </div>
    </section>
  );
}
